"use client";

import { formatMetricValue } from "@/lib/pagespeed";
import type { AnalyzeData } from "@/types/analyze";
import { Info, ExternalLink, Lock } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getAuditDescription } from "@/lib/psi-audit-descriptions";

interface PerformanceDetailsProps {
  device: "mobile" | "desktop";
  pageSpeed?: AnalyzeData["pageSpeed"];
  planType?: AnalyzeData["planType"];
}

interface PerformanceMetric {
  name: string;
  key: "lcp" | "fcp" | "tbt" | "cls" | "speedIndex";
  unit: string;
  auditId: string;
}

interface MetricSummary {
  points: {
    achieved: number;
    max: number;
  };
  value: number | null;
  unit: string;
  thresholds: {
    t1: number;
    t2: number;
  };
}

interface PerformanceSummary {
  categories: {
    performance?: {
      metrics: {
        lcp?: MetricSummary;
        fcp?: MetricSummary;
        tbt?: MetricSummary;
        cls?: MetricSummary;
        speedIndex?: MetricSummary;
      };
    };
  };
}

export function PerformanceDetails({
  device,
  pageSpeed,
  planType,
}: PerformanceDetailsProps) {
  const deviceData =
    device === "mobile" ? pageSpeed?.mobile : pageSpeed?.desktop;
  const performanceScore = Math.round(
    deviceData?.scores?.performanceScore ?? 0
  );
  const isPaidPlan = planType === "paid";

  const performanceMetrics: PerformanceMetric[] = [
    {
      name: "Largest Contentful Paint",
      key: "lcp",
      unit: "ms",
      auditId: "largest-contentful-paint",
    },
    {
      name: "First Contentful Paint",
      key: "fcp",
      unit: "ms",
      auditId: "first-contentful-paint",
    },
    {
      name: "Total Blocking Time",
      key: "tbt",
      unit: "ms",
      auditId: "total-blocking-time",
    },
    {
      name: "Cumulative Layout Shift",
      key: "cls",
      unit: "",
      auditId: "cumulative-layout-shift",
    },
    {
      name: "Speed Index",
      key: "speedIndex",
      unit: "ms",
      auditId: "speed-index",
    },
  ];

  const getPerformanceThresholds = (metricName: string) => {
    switch (metricName) {
      case "First Contentful Paint":
        return { t1: 1800, t2: 3000, max: 3600 };
      case "Largest Contentful Paint":
        return { t1: 2500, t2: 4000, max: 4800 };
      case "Cumulative Layout Shift":
        return { t1: 0.1, t2: 0.25, max: 0.35 };
      case "Total Blocking Time":
        return { t1: 200, t2: 400, max: 500 };
      case "Speed Index":
        return { t1: 3400, t2: 5400, max: 6500 };
      default:
        return { t1: 0, t2: 0, max: 1 };
    }
  };

  const formatMetricValueDisplay = (
    name: string,
    value?: number | null,
    unit?: string
  ) => {
    if (value === undefined || value === null) return "N/A";
    if (name === "Cumulative Layout Shift") return value.toFixed(2);
    return formatMetricValue(value, unit || "ms");
  };

  const PerformanceIndicator = ({
    metricName,
    metricValue,
  }: {
    metricName: string;
    metricValue?: number | null;
  }) => {
    const { t1, t2, max } = getPerformanceThresholds(metricName);
    if (metricValue === undefined || metricValue === null) return null;

    const clampedValue = Math.max(0, Math.min(max, metricValue));
    const positionPercentage = `${(clampedValue / max) * 100}%`;
    const t1Percentage = (t1 / max) * 100;
    const t2Percentage = (t2 / max) * 100;
    const gapPixels = 6;
    const performanceZone =
      clampedValue <= t1 ? "emerald" : clampedValue <= t2 ? "amber" : "red";

    return (
      <div className="mt-1 relative w-full">
        <div className="relative h-2 w-full">
          <span
            className="absolute left-0 top-0 h-2 rounded-l-full rounded-r-full bg-status-green/90"
            style={{ width: `calc(${t1Percentage}% - ${gapPixels / 2}px)` }}
          />
          <span
            className="absolute top-0 h-2 bg-background rounded-full"
            style={{
              left: `calc(${t1Percentage}% - ${gapPixels / 2}px)`,
              width: `${gapPixels}px`,
            }}
          />
          <span
            className="absolute top-0 h-2 bg-status-orange/90 rounded-l-full rounded-r-full"
            style={{
              left: `calc(${t1Percentage}% + ${gapPixels / 2}px)`,
              width: `calc(${t2Percentage - t1Percentage}% - ${gapPixels}px)`,
            }}
          />
          <span
            className="absolute top-0 h-2 bg-background rounded-full"
            style={{
              left: `calc(${t2Percentage}% - ${gapPixels / 2}px)`,
              width: `${gapPixels}px`,
            }}
          />
          <span
            className="absolute top-0 h-2 rounded-l-full rounded-r-full bg-status-red/90"
            style={{
              left: `calc(${t2Percentage}% + ${gapPixels / 2}px)`,
              width: `calc(${100 - t2Percentage}% - ${gapPixels / 2}px)`,
            }}
          />

          <span
            className="pointer-events-none absolute -top-4 -translate-x-1/2 text-[10px] text-muted-foreground"
            style={{ left: `calc(${t1Percentage}%)` }}
          >
            {formatMetricValueDisplay(metricName, t1)}
          </span>
          <span
            className="pointer-events-none absolute -top-4 -translate-x-1/2 text-[10px] text-muted-foreground"
            style={{ left: `calc(${t2Percentage}%)` }}
          >
            {formatMetricValueDisplay(metricName, t2)}
          </span>

          <span
            className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 -translate-x-1/2 rounded-full border bg-background shadow-sm ${
              performanceZone === "emerald"
                ? "border-emerald-500"
                : performanceZone === "amber"
                ? "border-amber-500"
                : "border-red-500"
            }`}
            style={{ left: positionPercentage }}
          />
        </div>
      </div>
    );
  };

  // Parse summary data if available
  const performanceSummary: PerformanceSummary | null = deviceData?.summary
    ? (deviceData.summary as PerformanceSummary)
    : null;

  const performanceMetricsData =
    performanceSummary?.categories?.performance?.metrics;

  return (
    <div className="rounded-md border border-border/50 p-3">
      <div className="mb-6 flex items-center justify-between">
        <div className="text-base font-bold text-foreground">Performance</div>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium">
          {performanceScore}/100
        </span>
      </div>
      {!deviceData ? (
        <div className="text-xs text-muted-foreground">
          No {device} PageSpeed details
        </div>
      ) : !performanceMetricsData ? (
        <div className="text-xs text-muted-foreground">
          Performance metrics are not available in the current data format.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {performanceMetrics.map((metric) => {
            const metricData = performanceMetricsData[metric.key];
            const metricValue = metricData?.value;
            const pointsData = metricData?.points;
            const { t1, t2 } = getPerformanceThresholds(metric.name);
            const isLockedMetric =
              !isPaidPlan &&
              Boolean(
                metricData &&
                  typeof metricData === "object" &&
                  (metricData as { locked?: boolean }).locked === true
              );
            const statusColor = isLockedMetric
              ? "bg-muted-foreground/40"
              : getMetricStatusColor(metric.name, metricValue, t1, t2);
            const auditDetails = getAuditDescription(metric.auditId);

            return (
              <div key={metric.name} className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${statusColor}`}
                    />
                    <div className="text-sm font-medium text-foreground">
                      {metric.name}
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                      </PopoverTrigger>
                      <PopoverContent className="w-80 max-w-[90vw]">
                        <div className="space-y-2">
                          <p className="text-sm font-medium">{auditDetails.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {auditDetails.description}
                          </p>
                          {auditDetails.link && (
                            <a
                              href={auditDetails.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-foreground hover:text-foreground/80 underline inline-flex items-center gap-1"
                            >
                              Learn more <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {isLockedMetric ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                      <Lock className="h-3 w-3" />
                      Locked
                    </span>
                  ) : pointsData ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                      {Math.round(pointsData.achieved)}/
                      {Math.round(pointsData.max)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                      No data
                    </span>
                  )}
                </div>
                {!isLockedMetric && (
                  <div className="space-y-2 pl-4">
                    <div className="text-sm font-medium text-foreground text-left">
                      {typeof metricValue === "number"
                        ? formatMetricValueDisplay(
                            metric.name,
                            metricValue,
                            metric.unit
                          )
                        : "No measurement captured"}
                    </div>
                    {typeof metricValue === "number" && (
                      <PerformanceIndicator
                        metricName={metric.name}
                        metricValue={metricValue}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getMetricStatusColor(
  metricName: string,
  value: number | null | undefined,
  t1: number,
  t2: number
): string {
  if (value === null || value === undefined) return "bg-muted-foreground/40";

  if (metricName === "Cumulative Layout Shift") {
    if (value <= t1) return "bg-status-green";
    if (value <= t2) return "bg-status-orange";
    return "bg-status-red";
  }

  if (value <= t1) return "bg-status-green";
  if (value <= t2) return "bg-status-orange";
  return "bg-status-red";
}
